<script setup lang="ts">
/**
 * 新デプロイ検知時に画面上部に表示されるバナー (GRAND Designer と同様)。
 * - ユーザーが「更新する」を押すまで持続表示 (toast と違って消えない)
 * - 「×」で一旦閉じる。次のポーリングで再検知はしないが、リロードするまで再表示もしない
 *   (検知後はポーリング停止のため。閉じた後に更新したい場合はブラウザ再読込)
 */
import { ref } from 'vue';
import { useDeployCheck } from '@/composables/useDeployCheck';

const { newVersionAvailable, reload } = useDeployCheck();
const dismissed = ref(false);

function onReload(): void {
  reload();
}

function onDismiss(): void {
  dismissed.value = true;
}
</script>

<template>
  <transition name="slide-up">
    <div
      v-if="newVersionAvailable && !dismissed"
      class="reload-banner"
      role="alert"
      aria-live="polite"
    >
      <v-icon
        icon="mdi-rocket-launch-outline"
        size="18"
        class="reload-icon"
      />
      <span class="reload-text">
        新しいバージョンが利用できます。ページを更新して最新版に切り替えてください。
      </span>
      <div class="reload-actions">
        <button
          type="button"
          class="reload-btn primary"
          @click="onReload"
        >
          <v-icon
            icon="mdi-refresh"
            size="16"
          />
          <span>更新する</span>
        </button>
        <button
          type="button"
          class="reload-btn secondary"
          aria-label="閉じる"
          @click="onDismiss"
        >
          <v-icon
            icon="mdi-close"
            size="16"
          />
        </button>
      </div>
    </div>
  </transition>
</template>

<style scoped>
.reload-banner {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  gap: var(--ds-space-sm);
  padding: 10px 16px;
  background: var(--ds-accent-gradient);
  color: #ffffff;
  /* 下部固定なので影は上向きに落とす */
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.35);
  font-size: var(--ds-font-sm);
}
.reload-icon {
  flex-shrink: 0;
}
.reload-text {
  flex: 1;
  line-height: 1.5;
}
.reload-actions {
  display: inline-flex;
  gap: var(--ds-space-xs);
  align-items: center;
}
.reload-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: none;
  cursor: pointer;
  font-size: var(--ds-font-sm);
  font-weight: var(--ds-font-semibold, 600);
  padding: 6px 14px;
  border-radius: var(--ds-radius-md);
  transition: all 0.15s ease;
}
.reload-btn.primary {
  background: #ffffff;
  color: var(--ds-accent-primary);
}
.reload-btn.primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
}
.reload-btn.secondary {
  background: rgba(255, 255, 255, 0.15);
  color: #ffffff;
  padding: 6px;
  width: 32px;
  height: 32px;
}
.reload-btn.secondary:hover {
  background: rgba(255, 255, 255, 0.25);
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition:
    transform 0.3s ease,
    opacity 0.3s ease;
}
.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

@media (max-width: 600px) {
  .reload-banner {
    flex-wrap: wrap;
    padding: 8px 10px;
  }
  .reload-text {
    width: 100%;
    font-size: var(--ds-font-xs);
  }
}
</style>
